/**
 * API client for user management endpoints.
 * Routes: /api/mgmt/users
 */

import { apiFetch } from './client';

export interface User {
  username: string;
  email: string;
  displayName: string;
  role: 'user' | 'admin';
  status: 'active' | 'disabled';
  maxBudget?: number;
  tpmLimit?: number;
  rpmLimit?: number;
  allowedCidrs?: string[];
  spend?: number;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserData {
  username: string;
  email: string;
  displayName: string;
  role: 'user' | 'admin';
  maxBudget?: number;
  tpmLimit?: number;
  rpmLimit?: number;
  allowedCidrs?: string[];
}

export interface UpdateUserData {
  email?: string;
  displayName?: string;
  role?: 'user' | 'admin';
  status?: 'active' | 'disabled';
  maxBudget?: number;
  tpmLimit?: number;
  rpmLimit?: number;
  allowedCidrs?: string[];
}

export async function listUsers(token: string): Promise<User[]> {
  const data = await apiFetch<{ users: User[] }>('/api/mgmt/users', token);
  return Array.isArray(data) ? data : (data.users ?? []);
}

export async function getUser(username: string, token: string): Promise<User> {
  const data = await apiFetch<{ user: User }>(`/api/mgmt/users/${encodeURIComponent(username)}`, token);
  return data.user ?? data as unknown as User;
}

export async function createUser(data: CreateUserData, token: string): Promise<User> {
  const resp = await apiFetch<{ user: User }>('/api/mgmt/users', token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return resp.user ?? resp as unknown as User;
}

export async function updateUser(
  username: string,
  data: UpdateUserData,
  token: string,
): Promise<User> {
  const resp = await apiFetch<{ user: User }>(`/api/mgmt/users/${encodeURIComponent(username)}`, token, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return resp.user ?? resp as unknown as User;
}

export async function disableUser(username: string, token: string): Promise<User> {
  return updateUser(username, { status: 'disabled' }, token);
}

export async function deleteUser(username: string, token: string): Promise<void> {
  await apiFetch<{ message: string }>(`/api/mgmt/users/${encodeURIComponent(username)}`, token, {
    method: 'DELETE',
  });
}

export interface UserActivity {
  timestamp: string;
  type: 'login' | 'request' | 'config_change';
  action: string;
  details: string;
  actor: string;
  target: string;
}

export interface BulkImportUser {
  username: string;
  email: string;
  displayName: string;
  role?: string;
  maxBudget?: number | string;
}

export interface BulkImportResult {
  created: string[];
  skipped: string[];
  errors: Array<{ row: number; username?: string; error: string }>;
}

export async function bulkImportUsers(users: BulkImportUser[], token: string): Promise<BulkImportResult> {
  return apiFetch<BulkImportResult>('/api/mgmt/users/bulk', token, {
    method: 'POST',
    body: JSON.stringify({ users }),
  });
}

export async function getUserActivity(username: string, token: string, limit = 20): Promise<UserActivity[]> {
  const data = await apiFetch<{ activity: UserActivity[] }>(
    `/api/mgmt/users/${encodeURIComponent(username)}/activity?limit=${limit}`,
    token,
  );
  return Array.isArray(data) ? data : (data.activity ?? []);
}
